/* eslint-disable ember/no-runloop */
import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { assert } from "@ember/debug";
import { registerDestructor } from "@ember/destroyable";
import type Owner from "@ember/owner";
import { getOwner } from "@ember/owner";
import { cancel, later, run } from "@ember/runloop";
import { service } from "@ember/service";
import { isTesting } from "@embroider/macros";
import { tracked } from "@glimmer/tracking";
import type Authenticator from "ember-simple-auth/authenticators/base";
import SessionService from "ember-simple-auth/services/session";
import Medic from "parog-web/models/medic";
import Patient from "parog-web/models/patient";
import { removeItemFromLocalStorage } from "parog-web/utils/use-local-storage";
import RSVP from "rsvp";
import ControlTower from "wise-control-tower";

/**
 * A subclass of the Ember Simple Auth session service.
 *
 * Ember Simple Auth provides a mechanism to  make sure a user is authenticated
 * before entering some routes, and the logic for authenticating a user.
 *
 * This subclass adds a step in the authentication process: the retrieval of the
 * authenticated medic or patient object, which contains useful information used
 * throughout the app.
 *
 * This subclass also overrides the default behaviour of the `authenticate` and
 * `handleAuthentication` methods. If the authentication is successful,
 * `authenticate` emits an event that is captured by the session service itself
 * to run a transition to the logged in route, though these events are considered
 * obsolete.
 * This has a drawback: we cannot make sure that the user is loaded before
 * entering authenticated routes.
 * When the event is emitted, it actually calls `handleAuthentication`. This subclass
 * overrides the method so that it does nothing if the medic or patient is not retrieved,
 * and an explicit call to this method is done in `authenticate` after the user is retrieved.
 * We thus have the expected behaviour on our side while still being able to use Ember Simple Auth
 * as expected.
 */
export default class SemeiaSessionService extends SessionService {
  @service declare requestManager;
  @service declare store: Store;

  @tracked isAuthenticatedAtThisSession = false;
  @tracked declare medic?: Medic & { uuid?: string };
  @tracked declare patient?: Patient;

  _refreshTokenTimeout;
  /**
   * This is a temporary storage of an access token.
   * It is used while authenticating (not restoring a session): when authenticating,
   * the token is retrieved, then a call to fetchAuthenticatedUser is made.
   * But the whole authentication processed is not finished, thus this.session.authenticated is empty.
   * This attribute is used in the application adapter for authenticated requests.
   * `access_token` is used as a temporary storage, used instead of this.session.authenticated if present.
   */
  access_token?: string;

  /**
   * During the login steps `data.authenticated.access_token` is null but `this.access_token` is set
   * For all other requests `data.authenticated.access_token` will be set and used
   */
  get accessToken() {
    if (!this.isAuthenticated && !this.access_token) return;

    return this.data.authenticated.access_token || this.access_token;
  }

  get logoutUrl(): string {
    return "/medics-web/logout";
  }

  get productName() {
    if (this.user instanceof Patient) {
      return this.user?.healthCentre?.productNameCapitalized ?? "Wise";
    }
    if (this.user instanceof Medic) {
      return this.user?.primaryHealthCenter?.productNameCapitalized ?? "Wise";
    }
    return "Wise";
  }

  get refreshToken() {
    if (!this.isAuthenticated) return;

    return this.data.authenticated.refresh_token;
  }

  // ===== Public API =====
  get user(): ModelRegistry["medic"] | ModelRegistry["patient"] | undefined {
    return this.medic ?? this.patient;
  }

  /** True once this tab has been elected the cross-tab refresh leader. */
  private _isRefreshLeader = false;
  /** Guards against requesting the leader lock more than once. */
  private _isRefreshLeadershipRequested = false;
  /** Releases the leader lock (resolves the held Web Locks promise). */
  private _releaseRefreshLeadership?: () => void;

  private get _selectedFilterKey(): string {
    const key = "medics-web.protected.updates.selectedFilter";
    if (isTesting()) {
      return `test-${key}`;
    }
    return key;
  }

  constructor(owner: Owner) {
    super(owner);
    registerDestructor(this, () => this._resignRefreshLeadership());
  }

  /**
   * Utility method for fetching an authenticated user.
   */
  async fetchAuthenticatedUser(data: {
    as: string;
    id: number | string;
    access_token?: string;
  }): Promise<void> {
    assert(
      "data.as must be patient or medic",
      ["patient", "medic"].includes(data.as)
    );
    // This assert is to ensure we don't see https://semeia.sentry.io/issues/5743948091 again.
    assert("data.id is set", data.id);

    this.access_token = data.access_token;

    const authenticatedAs = data.as;

    if (authenticatedAs === "medic") {
      this.medic = await this.store.findRecord("medic", data.id, {
        include: "health-centres.dmp-health-centre-config"
      });
    } else if (authenticatedAs === "patient") {
      this.patient = await this.store.findRecord("patient", data.id, {
        include: "health-centre-cohorts.health-centre"
      });
    }
  }

  override handleInvalidation(routeAfterInvalidation): void {
    removeItemFromLocalStorage(this._selectedFilterKey);

    if (isTesting()) {
      // When testing, Ember Simple Auth does not reload the page. Thus in-memory variable must be reset.
      this.medic = undefined;
      this.patient = undefined;
      this.isAuthenticatedAtThisSession = false;
    }

    super.handleInvalidation(routeAfterInvalidation);
  }

  override invalidate(...args) {
    if (this.data.authenticated.as == "medic") {
      void this.logoutMedicFromOpenIdConnect();
    }

    return super.invalidate(args);
  }

  isTokenValid(token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTimestamp = payload.exp * 1000;
      const todayTimestamp = new Date().getTime();

      return todayTimestamp < expirationTimestamp;
    } catch (e) {
      ControlTower.captureException(e);
      return false;
    }
  }

  async logoutMedicFromOpenIdConnect() {
    const body = JSON.stringify({
      open_id_refresh_token: this.data.authenticated.open_id_refresh_token,
      open_id_identity_provider: this.data.authenticated.open_id_issuer,
      access_token: this.accessToken
    });

    try {
      await this.requestManager.request({
        url: "/api/v1/medic_logout",
        method: "POST",
        body
      });
    } catch (_e) {
      // do nothing
    }
  }

  /**
   * Calls the refresh endpoint and updates the session with the tokens it
   * returns (a new access token, and possibly a new refresh token).
   */
  async refreshTokens() {
    const { content } = await this.requestManager.request({
      url: "/api/v1/refresh_token",
      method: "POST",
      body: JSON.stringify({
        refresh_token: this.refreshToken
      })
    });
    const {
      access_token,
      refresh_token
    }: { access_token: string; refresh_token?: string } = content;

    if (!access_token) {
      ControlTower.captureMessage(
        "AccessToken refresh error: empty AccessToken in response"
      );
    }

    this.updateSessionTokens(access_token, refresh_token);
  }

  async revokeToken(): Promise<void> {
    if (this.refreshToken) {
      const body = JSON.stringify({
        refresh_token: this.refreshToken
      });

      await this.requestManager.request({
        url: "/api/v1/revoke_token",
        method: "POST",
        body: body
      });
    }
  }

  // ===== Private =====
  /**
   * Called by the authenticators after authentication / restore, and after every
   * successful refresh. It makes this tab take part in the cross-tab "refresh
   * leader" election; only the elected leader arms the timer and calls the
   * refresh endpoint. Non-leader tabs do nothing here — they receive the
   * refreshed tokens through ESA's `storage` synchronisation.
   *
   * Electing a single leader removes the cross-tab refresh race at its source:
   * never more than one tab refreshes at a time. Previously, concurrent
   * refreshes from several tabs could make one of them fail and log every tab
   * out.
   */
  scheduleNextRefresh(accessToken) {
    if (isTesting()) return;

    // Web Locks unavailable (legacy browser / insecure context): fall back to the
    // historical behaviour where every tab refreshes on its own timer.
    if (!navigator.locks) {
      this._armRefreshTimer(accessToken);
      return;
    }

    // Already the leader: just (re)arm the timer for the next cycle.
    if (this._isRefreshLeader) {
      this._armRefreshTimer(accessToken);
      return;
    }

    // Not the leader yet — request leadership exactly once. The callback runs only
    // when we win it: immediately if no tab holds it, or later when the current
    // leader's tab closes/crashes and the browser releases its lock.
    if (this._isRefreshLeadershipRequested) return;
    this._isRefreshLeadershipRequested = true;

    void navigator.locks.request(
      "token_refresh_leader",
      () =>
        // Held for the lifetime of the tab. We never resolve this promise, so the
        // lock stays ours until teardown / logout (or the browser reclaims it on
        // tab close), at which point the next waiting tab takes over.
        new Promise<void>(release => {
          this._isRefreshLeader = true;
          this._releaseRefreshLeadership = release;
          this._armRefreshTimer(this.accessToken);
        })
    );
  }

  /** Arms the one-shot timer that refreshes shortly before the token expires. */
  private _armRefreshTimer(accessToken) {
    if (!accessToken) return;

    const expiresAt = this.tokenExpiresAt(accessToken);
    const now = new Date().getTime();
    const offset = this.tokenRefreshOffset();

    if (expiresAt <= now - offset) return;

    cancel(this._refreshTokenTimeout);
    delete this._refreshTokenTimeout;
    this._refreshTokenTimeout = later(
      this,
      this.refreshTokensAndScheduleNext,
      expiresAt - now - offset
    );
  }

  /** Releases the leader lock + timer so another tab can take over. */
  private _resignRefreshLeadership() {
    cancel(this._refreshTokenTimeout);
    delete this._refreshTokenTimeout;
    this._releaseRefreshLeadership?.();
    this._releaseRefreshLeadership = undefined;
    this._isRefreshLeader = false;
    this._isRefreshLeadershipRequested = false;
  }

  private refreshTokensAndScheduleNext() {
    return new RSVP.Promise(() => {
      this.refreshTokens().then(
        () => {
          run(() => {
            this.scheduleNextRefresh(this.accessToken);
          });
        },
        async response => {
          await this.invalidate();
          throw `Access token could not be refreshed - server responded with ${response.responseJSON}.`;
        }
      );
    });
  }

  private tokenExpiresAt(token) {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000;
  }

  private tokenRefreshOffset(): number {
    const min = 5;
    const max = 10;

    return (Math.floor(Math.random() * (max - min)) + min) * 1000;
  }

  private updateSessionTokens(accessToken: string, refreshToken?: string) {
    if (!this.isAuthenticated) return;

    const authenticatorName = this.data.authenticated
      .authenticator as `${string}:${string}`;

    const authenticator = (getOwner(this) as Owner).lookup(
      authenticatorName
    ) as Authenticator;

    authenticator.trigger("sessionDataUpdated", {
      ...this.data.authenticated,
      access_token: accessToken,
      ...(refreshToken !== undefined && { refresh_token: refreshToken })
    });
  }
}

export interface SemeiaMedicSession extends SemeiaSessionService {
  medic: Medic;
}

export interface SemeiaPatientSession extends SemeiaSessionService {
  patient: Patient;
}

declare module "@ember/service" {
  interface Registry {
    session: SemeiaSessionService;
  }
}
